import { isEmpty, path, pathOr } from 'ramda';
import { useMemo } from 'react';
import { ItemType } from '../../../constants/ItemType';
import { ExternalSource, MainSource, Source } from '../../../constants/Source';
import { memoizePromise } from '../../memoize';
import { useAdapter } from '../useAdapter';

type TMethodsPaths = {
  [key: string]: {
    [key: string]: string[];
  };
};

const filesMethodsPathList = { [ItemType.File]: ['items', 'list'] };
const foldersMethodsPathList = { [ItemType.Folder]: ['folders', 'list'] };
const standardMethodsPathsList = { ...foldersMethodsPathList, ...filesMethodsPathList };

export const LIST_METHODS_PATHS: TMethodsPaths = {
  [MainSource.SiteFiles]: standardMethodsPathsList,
  [MainSource.VisitorsUploads]: standardMethodsPathsList,
  [MainSource.Recommended]: standardMethodsPathsList,
  [MainSource.Trash]: {
    [ItemType.Folder]: ['folders', 'trash', 'list'],
    [ItemType.File]: ['items', 'trash', 'list'],
  },
  [MainSource.Home]: filesMethodsPathList,
  [MainSource.MediaFromWix]: standardMethodsPathsList,
  [MainSource.NTT]: standardMethodsPathsList,
  [MainSource.Yell]: standardMethodsPathsList,
  [MainSource.Shutterstock]: standardMethodsPathsList,
  [MainSource.Unsplash]: standardMethodsPathsList,
  [MainSource.Boards]: filesMethodsPathList,
  [ExternalSource.Facebook]: standardMethodsPathsList,
  [ExternalSource.Vimeo]: standardMethodsPathsList,
  [ExternalSource.Dribbble]: standardMethodsPathsList,
  [ExternalSource.GoogleDrive]: standardMethodsPathsList,
  [ExternalSource.Dropbox]: standardMethodsPathsList,
  [ExternalSource.Instagram]: standardMethodsPathsList,
  [ExternalSource.Flickr]: standardMethodsPathsList,
  [ExternalSource.DeviantArt]: standardMethodsPathsList,
  [ExternalSource.WixSite]: standardMethodsPathsList,
  [ExternalSource.GooglePhotos]: standardMethodsPathsList,
};

const filesMethodsPathSearch = { [ItemType.File]: ['items', 'search'] };
const foldersMethodsPathSearch = { [ItemType.Folder]: ['folders', 'search'] };
const standardMethodsPathsSearch = { ...foldersMethodsPathSearch, ...filesMethodsPathSearch };

export const SEARCH_METHODS_PATHS: TMethodsPaths = {
  [MainSource.All]: standardMethodsPathsSearch,
  [MainSource.SiteFiles]: standardMethodsPathsSearch,
  [MainSource.Trash]: {
    [ItemType.Folder]: ['folders', 'trash', 'search'],
    [ItemType.File]: ['items', 'trash', 'search'],
  },
  [MainSource.VisitorsUploads]: filesMethodsPathSearch,
  [MainSource.MediaFromWix]: {
    [ItemType.File]: ['items', 'multiSearch'],
  },
  [MainSource.Shutterstock]: filesMethodsPathSearch,
  [MainSource.Unsplash]: filesMethodsPathSearch,
  [ExternalSource.WixSite]: standardMethodsPathsSearch,
  [ExternalSource.WixVideo]: filesMethodsPathSearch,
};

const filesMethodsPathUpdate = { [ItemType.File]: ['item', 'update'] };
const foldersMethodsPathUpdate = { [ItemType.Folder]: ['folder', 'update'] };
const standardMethodsPathsUpdate = { ...foldersMethodsPathUpdate, ...filesMethodsPathUpdate };

export const UPDATE_METHODS_PATHS: TMethodsPaths = {
  [MainSource.SiteFiles]: standardMethodsPathsUpdate,
  [MainSource.VisitorsUploads]: standardMethodsPathsUpdate,
};

const standardMethodsPathsCreate = { [ItemType.Folder]: ['folder', 'create'] };

export const CREATE_NEW_FOLDER_METHODS_PATHS: TMethodsPaths = {
  [MainSource.SiteFiles]: standardMethodsPathsCreate,
  [MainSource.VisitorsUploads]: standardMethodsPathsCreate,
};

const standardMethodsPathsGetDownloadURL = { [ItemType.File]: ['item', 'getDownloadUrl'] };

export const GET_DOWNLOAD_URL_METHODS_PATHS: TMethodsPaths = {
  [MainSource.SiteFiles]: standardMethodsPathsGetDownloadURL,
  [MainSource.VisitorsUploads]: standardMethodsPathsGetDownloadURL,
};

export function getAdapterMethod<T>(
  methodsPaths: TMethodsPaths,
  source: Source,
  itemType: ItemType,
  adapter: any,
): (...args: any[]) => T {
  const methodPath = pathOr<[], []>([], [source, itemType], methodsPaths);

  if (isEmpty(methodPath)) {
    return () => Promise.resolve({ data: [] }) as unknown as T;
  }

  return path(methodPath, adapter);
}

export const useCachedUserInfoLoader = () => {
  const adapter = useAdapter(MainSource.SiteFiles);

  return useMemo(() => memoizePromise((userId) => adapter.user.get(userId, { correlationId: null })), [adapter]);
};
